import { PropsWithChildren } from 'react';
import { useStore } from '@tanstack/react-form';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '@utilities-one/gpm-ui';

import { useFieldContext, useFormContext } from '@/library/form/contexts';
import { FormControlProps } from '@/library/form/types/form.type';

type FormBaseProps = PropsWithChildren<FormControlProps>;

/** Shared label / description / error chrome around a single form control (gpm-webapp-v2 pattern). */
export const FormBase = ({
  children,
  label,
  labelAccessory,
  description,
  hint,
  showLabel = true,
  isRequired = false,
}: FormBaseProps) => {
  const field = useFieldContext<unknown>();
  const form = useFormContext();
  const submitAttempted = useStore(form.store, (state) => state.submissionAttempts > 0);
  const isInvalid = (field.state.meta.isTouched || submitAttempted) && !field.state.meta.isValid;

  const fieldLabel = (
    <FieldLabel htmlFor={field.name} required={isRequired}>
      {label}
    </FieldLabel>
  );

  return (
    <Field data-invalid={isInvalid}>
      {showLabel && (
        <FieldContent>
          {labelAccessory ? (
            <div className="flex items-center justify-between gap-2">
              {fieldLabel}
              {labelAccessory}
            </div>
          ) : (
            fieldLabel
          )}
          {description && <FieldDescription>{description}</FieldDescription>}
        </FieldContent>
      )}
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      {isInvalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  );
};
